import { isEmpty, isNull } from "lodash";
import { DateTime } from "luxon";
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";

// Imported actions
import { getChargingUnitsByStationId } from "../../../../actions/charge.js";
import { postMaintenanceRecord } from "../../../../actions/maintenance.js";

// Imported assets
import blueIcon from "../../../../assets/icons/LightningBoltIcon_Blue.svg";
import closeIcon from "../../../../assets/icons/close.svg";
import greenIcon from "../../../../assets/icons/LightningBoltIcon_Green.svg";
import redIcon from "../../../../assets/icons/LightningBoltIcon_Red.svg";
import yellowIcon from "../../../../assets/icons/LightningBoltIcon_Yellow.svg";

// Imported components
import PrimaryButton from "../../../../components/ui/PrimaryButton.js";

// Imported contexts
import { useUserContext } from "../../../../contexts/User.tsx";

// Imported utils
import { getFromLocalStorage } from "../../../../utils/storage.js";
import { isLoggedIn } from "../../../../utils/user.js";

// Keyframe for the sliding animation
const slideOut = keyframes`
  0% {
    right: 0;
  }
  100% {
    right: -400px;  /* Moves the component 400px to the right (off screen) */
  }
`;

const slideIn = keyframes`
  0% {
    right: -400px;
  }
  100% {
    right: 0;
  }
`;

interface ContainerProps {
  $isClosing?: boolean;
}

const Container = styled.div<ContainerProps>`
  animation: ${({ $isClosing }) => ($isClosing ? slideOut : slideIn)} 0.5s
    forwards;
  background-color: #fff;
  border-left: 1px solid #d9d9d9;
  border-radius: 0;
  box-shadow: -2px 0px 5px rgba(0, 0, 0, 0.1);
  height: calc(100vh - 80px); // 100% viewport height - height of nav bar (80px)
  overflow-x: hidden;
  overflow-y: auto;
  position: absolute;
  width: 400px;
  z-index: 10;
`;

interface HeaderProps {
  $maintenanceStatus?: boolean;
  $stationStatus?: string;
}

const Header = styled.div<HeaderProps>`
  ${({ $maintenanceStatus, $stationStatus }) => {
    // if ($stationStatus === "Cyber Attack") {
    //   return `
    //     background: #fbc900;
    //     color: black;
    //   `;
    // } else if ($stationStatus === "Faulted") {
    //   return `
    //     background: #f0170b;
    //     color: white;
    //   `;
    // } else if ($maintenanceStatus) {
    //   return `
    //     background: #1e1efc;
    //     color: white;
    //   `;
    // } else {
    //   // $stationStatus === "Available" OR default
    //   return `
    //     background: #12c300;
    //     color: black;
    //   `;
    // }
    if ($stationStatus === "Cyber Attack") {
      return `
        background: #fbc900;
        color: black;
      `;
    } else if ($maintenanceStatus) {
      return `
        background: #001efb;
        color: white;
      `;
    } else if ($stationStatus === "Faulted") {
      return `
        background: #c10a00;
        color: white;
      `;
    } else {
      // $stationStatus === "Available" OR default
      return `
        background: #12c300;
        color: black;
      `;
    }
  }}
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #d9d9d9;
`;

const HeaderIcon = styled.img`
  margin-left: 10px;
  margin-right: 15px;
  width: 50px;
`;

const HeaderText = styled.h2`
  color: white;
  font-family: Oxygen, Inter, Roboto, sans-serif;
  font-weight: 300;
`;

const StationInfoContainer = styled.div`
  background: white;
  border-bottom: 1px solid #d9d9d9;
  font-family: Inter, Oxygen, Roboto, sans-serif !important;
  padding: 10px;
  position: relative;
`;

const CloseButton = styled.img`
  background-color: transparent;
  border: none;
  color: black;
  cursor: pointer;
  font-size: 16px;
  margin: 0 5px;
  width: 50px;
`;

const ChargingUnitsListingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-family: Inter, Oxygen, Roboto, sans-serif !important;
  overflow-y: auto;
`;

const ChargingUnitListing = styled.div`
  background: rgba(240, 240, 240, 1);
  border-bottom: 1px solid #d9d9d9;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  padding: 10px 0;
  width: 100%;
`;

const ChargingUnitStatusIcon = styled.img`
  margin: 0 10px;
  width: 40px;
`;

const ChargingUnitStatusDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChargingUnitListingExpandIcon = styled.div`
  align-self: center;
  margin-left: auto;
  margin-right: 10px;
`;

const ChargingUnitInfoContainer = styled.div`
  border-bottom: 1px solid #d9d9d9;
  display: flex;
  flex-direction: column;
  padding: 10px;
`;

const ChargingUnitPortInfoContainer = styled.div`
  border-top: 1px solid #d9d9d9;
  display: flex;
  flex-direction: column;
  padding: 10px 0;
`;

const ChargingUnitConnectorInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoHeader = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  margin: 5px 0;
  text-align: center;
`;

const InfoHeaderText = styled.span`
  // border: 1px solid #D9D9D9;
  border-radius: 8px;
  padding: 3px;
`;

const ActionButton = styled(Link)`
  align-self: center;
  background: var(--terbine-blue);
  border-radius: 8px;
  color: #fff;
  font-family: Inter, Oxygen, Roboto, sans-serif !important;
  font-size: 18px;
  margin: 8px;
  padding: 8px 64px;

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const ViewUptimeButton = styled(PrimaryButton)`
  background: #f2f2f2;
  border: none;
  border-radius: 8px;
  // bottom: 10px;
  color: black;
  cursor: pointer;
  height: 24px;
  margin: 8px 0;
  // position: absolute;
  // right: 10px;
  width: auto;

  &:hover {
    background: lab(from #f2f2f2 calc(l - 5) a b / 100);
  }
`;

const InfoView = ({ stationData, onClose }) => {
  const { user } = useUserContext();
  const appLocation = useLocation();
  const navigate = useNavigate();

  // closing animation
  const [isClosing, setIsClosing] = useState<boolean>(false);

  // State for
  // const [chargingUnits, setChargingUnits] = useState([]);

  const [expandedUnit, setExpandedUnit] = useState<string>("");
  const [expandedUnitPorts, setExpandedUnitPorts] = useState<boolean>(true);

  // closeable only if the onClose function is provided
  const closeable = Boolean(onClose);

  const fullAddress = `${stationData.addressLine1}${
    stationData.addressLine2 ? `, ${stationData.addressLine2}` : ""
  }${stationData.city ? `, ${stationData.city}` : ""}${
    stationData.state ? `, ${stationData.state}` : ""
  }${stationData.postalCd ? `, ${stationData.postalCd}` : ""}${
    stationData.countryCd ? `, ${stationData.countryCd}` : ""
  }`;

  const handleClose = () => {
    setIsClosing(true); // Start the closing animation
  };

  // Trigger the `onClose` after the animation ends
  useEffect(() => {
    if (isClosing) {
      const timer = setTimeout(() => {
        onClose(); // Call the parent onClose function after animation
      }, 500); // Matches the duration of the animation (0.5s)
      return () => clearTimeout(timer); // Cleanup the timer
    }
  }, [isClosing, onClose]);

  // Grabs data for related charging units on load or station change
  // useEffect(() => {
  //   setChargingUnits([]);

  //   const getUnitsByStation = async () => {
  //     await getChargingUnitsByStationId(stationData.id)
  //       .then((units) => {
  //         setChargingUnits(units);
  //       })
  //       .catch((err) => {
  //         // handle error
  //       });
  //   };

  //   if (!isLoggedIn()) {
  //     navigate("/login", {
  //       replace: true,
  //       state: { path: appLocation.pathname },
  //     });
  //   } else getUnitsByStation();
  // }, [stationData]);

  // const chooseHeaderIcon = (
  //   stationStatus: string,
  //   maintenanceStatus: boolean
  // ) => {
  //   // if (stationStatus === "Cyber Attack") {
  //   //   return yellowIcon;
  //   // } else if (stationStatus === "Faulted") {
  //   //   return redIcon;
  //   // } else if (maintenanceStatus) {
  //   //   return blueIcon;
  //   // } else {
  //   //   // default or stationStatus === "Available"
  //   //   return greenIcon;
  //   // }
  //   if (stationStatus === "Cyber Attack") {
  //     return yellowIcon;
  //   } else if (maintenanceStatus) {
  //     return blueIcon;
  //   } else if (stationStatus === "Faulted") {
  //     return redIcon;
  //   } else {
  //     // default or stationStatus === "Available"
  //     return greenIcon;
  //   }
  // };

  const chooseUnitListingIcon = (
    unitStatus: string,
    maintenanceStatus: any[] | null
  ) => {
    if (unitStatus === "Cyber Security Incident") {
      return yellowIcon;
    } else if (!isNull(maintenanceStatus) && !isEmpty(maintenanceStatus)) {
      return blueIcon;
    } else if (unitStatus === "Faulted") {
      return redIcon;
    } else {
      return greenIcon;
    }
  };

  return (
    <Container $isClosing={isClosing}>
      <Header
        $maintenanceStatus={stationData.maintenanceActive}
        $stationStatus={stationData.locationStatusType.codeName}
      >
        {/* <HeaderIcon
          src={chooseHeaderIcon(
            stationData.locationStatusType.codeName,
            stationData.maintenanceActive
          )}
        /> */}
        <HeaderText>{stationData.locName}</HeaderText>
        {/* {closeable && <CloseButton onClick={handleClose} src={closeIcon} />} */}
      </Header>

      <StationInfoContainer>
        <p>
          <b>Station ID:</b> {stationData.externalId || "Unknown"}
        </p>
        <p>
          <b>Address:</b> {fullAddress || "Unknown"}
        </p>
        <p>
          <b>Coordinates:</b>{" "}
          {stationData.lat && stationData.lon
            ? `${stationData.lat}, ${stationData.lon}`
            : "Unknown"}
        </p>
        <p>
          <b>Network:</b> {user?.orgName || "Unknown"}
        </p>
        <p>
          <b>Access Type:</b> {stationData.accessType.codeName || "Unknown"}
        </p>
        <p>
          <b>Location Type:</b> {stationData.locationType.codeName || "Unknown"}
        </p>
        <p>
          <b>Available Charger(s):</b>{" "}
          {!isEmpty(stationData.chargeUnits) &&
            stationData.chargeUnits.filter(
              (unit) => unit.status.codeName === "Available"
            ).length}
          /{!isEmpty(stationData.chargeUnits) && stationData.chargeUnits.length}
        </p>
        <ViewUptimeButton onClick={() => {}} type="button">
          View Uptime
        </ViewUptimeButton>
      </StationInfoContainer>

      <ChargingUnitsListingsContainer>
        {!isEmpty(stationData.chargeUnits) &&
          stationData.chargeUnits
            .sort((a, b) => {
              const getUnitVal = (unit) => {
                if (unit.status.id === 10) {
                  // Cyber Security Incident
                  return 4;
                } else if (unit.status.id === 3) {
                  // Faulted
                  return 3;
                } else if (!isEmpty(unit.maintenanceInfos)) {
                  // Under Maintenance
                  return 2;
                } else {
                  // Available, and default or other
                  return 1;
                }
              };

              return getUnitVal(b) - getUnitVal(a);
            })
            .map((unit, i) => {
              return (
                <div key={`charging-unit-listing-${i}`}>
                  <ChargingUnitListing
                    onClick={() => {
                      setExpandedUnit(expandedUnit === unit.id ? "" : unit.id);
                      setExpandedUnitPorts(false);
                    }}
                  >
                    <ChargingUnitStatusIcon
                      src={chooseUnitListingIcon(
                        unit.status.codeName,
                        unit.maintenanceInfos
                      )}
                    />
                    <ChargingUnitStatusDetails>
                      <span>
                        <b>Charger ID:</b> {unit.externalId || "Unknown"}
                      </span>
                      <span>
                        <b>Status:</b>{" "}
                        {unit.status.codeName === "Cyber Security Incident"
                          ? "Cyber Security Attack Detected"
                          : unit.status.codeName === "Faulted"
                          ? unit.displayStatus
                          : "Unknown"}
                      </span>
                    </ChargingUnitStatusDetails>
                    <ChargingUnitListingExpandIcon>
                      {expandedUnit === unit.id ? "⏶" : "⏷"}
                    </ChargingUnitListingExpandIcon>
                  </ChargingUnitListing>
                  {expandedUnit === unit.id && (
                    <ChargingUnitInfoContainer>
                      {!isNull(unit.maintenanceInfos) &&
                        !isEmpty(unit.maintenanceInfos) && (
                          <>
                            <span>
                              <b>Maintenance Requested:</b>{" "}
                              {unit.maintenanceInfos[0].createdAt
                                ? DateTime.fromISO(
                                    unit.maintenanceInfos[0].createdAt
                                  ).toLocaleString(DateTime.DATE_SHORT) +
                                  " " +
                                  DateTime.fromISO(
                                    unit.maintenanceInfos[0].createdAt
                                  ).toLocaleString(DateTime.TIME_WITH_SECONDS)
                                : "Unknown"}
                            </span>
                            <span style={{ marginBottom: "16px" }}>
                              <b>Maintenance Status:</b>{" "}
                              {unit.maintenanceInfos[0].status.codeName ||
                                "Unknown"}
                            </span>
                          </>
                        )}
                      <span>
                        <b>Entered Service:</b>{" "}
                        {DateTime.fromISO(unit.installedAt).toLocaleString(
                          DateTime.DATE_SHORT
                        ) /*+
                          " " +
                          DateTime.fromISO(unit.installedAt).toLocaleString(
                            DateTime.TIME_WITH_SECONDS
                          )*/ || "Unknown"}
                      </span>
                      <span>
                        <b>Type:</b> {unit.unitType.codeName || "Unknown"}
                      </span>
                      <span>
                        <b>Manufacturer:</b>{" "}
                        {unit.model.manufacturer.name || "Unknown"}
                      </span>
                      <span>
                        <b>Model:</b> {unit.model.name || "Unknown"}
                      </span>
                      <span>
                        <b>Version:</b> {unit.model.version || "Unknown"}
                      </span>
                      {unit.status.codeName === "Faulted" ||
                      unit.status.codeName === "Cyber Security Incident" ? (
                        <span>
                          <b>Fault Type:</b>{" "}
                          {unit.status.codeName === "Cyber Security Incident"
                            ? "Cyber Security Incident"
                            : unit.displayFaultType || "Unknown"}
                        </span>
                      ) : null}
                      <span>
                        <b>Uptime:</b>{" "}
                        {unit.uptimePercentage
                          ? "" +
                            parseFloat(unit.uptimePercentage).toFixed(2) +
                            "%"
                          : "Unknown"}
                      </span>
                      <span>
                        <b>MTBF:</b> {unit.mtbf || "Unknown"}
                      </span>
                      {unit.status.codeName === "Cyber Security Incident" ||
                      unit.status.codeName === "Faulted" ||
                      unit.status.codeName === "Under Maintenance" ? (
                        <ActionButton
                          onClick={() => {
                            if (!isLoggedIn()) {
                              navigate("/login", {
                                replace: true,
                                state: { path: appLocation.pathname },
                              });
                            } else if (
                              unit.status.codeName === "Faulted" &&
                              (isNull(unit.maintenanceInfos) ||
                                isEmpty(unit.maintenanceInfos))
                            ) {
                              postMaintenanceRecord(unit.id);
                            }
                          }}
                          target="_blank"
                          to={
                            unit.displayFaultType ===
                            "Electromechanical Failure"
                              ? "/maximo"
                              : unit.status.codeName ===
                                "Cyber Security Incident"
                              ? "/cyber-security-platform"
                              : ""
                          }
                        >
                          {unit.status.codeName === "Cyber Security Incident"
                            ? "View Report"
                            : unit.displayFaultType ===
                                "Account Authentication Failure" ||
                              unit.displayFaultType ===
                                "Car to Charger Non-Sync" ||
                              unit.displayFaultType ===
                                "Charger to Operator Non-Sync" ||
                              unit.displayFaultType ===
                                "Payment Processing Failure" ||
                              unit.displayFaultType === "Misc Error"
                            ? "Live Chat"
                            : unit.displayFaultType === "Communication Failure"
                            ? "Carrier Feed"
                            : unit.displayFaultType ===
                              "Vandalism or Other Physical Issue"
                            ? "Live Feed"
                            : unit.displayFaultType === "Power Failure"
                            ? "Utility Feed"
                            : unit.displayFaultType ===
                              "Electromechanical Failure"
                            ? "Maintenance"
                            : unit.displayFaultType === "Unit Off or Offline"
                            ? "Force Reboot"
                            : unit.displayFaultType === "Weather"
                            ? "Local Conditions"
                            : "Maintenance"}
                        </ActionButton>
                      ) : null}
                      <InfoHeader
                        onClick={() => {
                          setExpandedUnitPorts(!expandedUnitPorts);
                        }}
                      >
                        <InfoHeaderText>
                          Ports
                          {expandedUnitPorts ? " ⏶" : " ⏷"}
                        </InfoHeaderText>
                      </InfoHeader>
                      {expandedUnitPorts &&
                        unit.ports.map((port, i) => (
                          <ChargingUnitPortInfoContainer
                            key={`charging-unit-port-${i}`}
                          >
                            <span>
                              <b>Port ID:</b> {port.externalId || "Unknown"}
                            </span>
                            <span>
                              <b>Port Location:</b> {port.locText || "Unknown"}
                            </span>
                            <span>
                              <b>Port Status:</b>{" "}
                              {port.status.codeName || "Unknown"}
                            </span>
                            <span>
                              <b>Port Max Output:</b>{" "}
                              {port.maxPower || "Unknown"}
                            </span>
                            {port.connectors.map((connector, j) => (
                              <ChargingUnitConnectorInfoContainer
                                key={`charging-unit-connector-${i}-${j}`}
                              >
                                <span>
                                  <b>Connector ID:</b>{" "}
                                  {connector.externalId || "Unknown"}
                                </span>
                                <span>
                                  <b>Connector Type:</b>{" "}
                                  {connector.connectorType.codeName ||
                                    "Unknown"}
                                </span>
                                <span>
                                  <b>Connector Status:</b>{" "}
                                  {connector.status.codeName || "Unknown"}
                                </span>
                                {/* <span>
                                  Connector Max Output:{" "}
                                  {connector.connectorType.maxOutput || "Unknown"}
                                </span> */}
                              </ChargingUnitConnectorInfoContainer>
                            ))}
                          </ChargingUnitPortInfoContainer>
                        ))}
                    </ChargingUnitInfoContainer>
                  )}
                </div>
              );
            })}
      </ChargingUnitsListingsContainer>
    </Container>
  );
};

export default InfoView;
